/** Agents console — the needs-you feed and run reports posted by Agent
 *  HQ routines. Access is a per-user flag (`can_view_agents` on /auth/me);
 *  every endpoint 404s without it, so hooks here are only mounted from
 *  the flag-gated page. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";

export interface AgentItem {
  key: string;
  kind: "urgent" | "decide" | "kicker" | "fyi";
  title: string;
  detail: string | null;
  link: string | null;
  project_id: string | null;
  fs_ticket_id: number | null;
  report_id: number;
  agent: string;
  reported_at: string;
  ack: { acked_by: string | null; acked_at: string } | null;
}

export interface AgentRun {
  id: number;
  agent: string;
  run_kind: "scheduled" | "manual";
  status: "ok" | "blocked" | "error";
  headline: string | null;
  created_at: string;
}

export interface AgentsFeed {
  items: AgentItem[];
  runs: AgentRun[];
}

export function useAgentsFeed() {
  return useQuery<AgentsFeed>({
    queryKey: ["agents", "feed"],
    queryFn: () => api.get("/agents/feed").then((r) => r.data),
  });
}

export interface AgentReport extends AgentRun {
  html: string | null;
  items: unknown[];
}

export function useAgentReport(reportId: number | null) {
  return useQuery<AgentReport>({
    queryKey: ["agents", "report", reportId],
    queryFn: () => api.get(`/agents/reports/${reportId}`).then((r) => r.data),
    enabled: reportId != null,
  });
}

export function useAckAgentItem() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemKey: string) =>
      api.post("/agents/items/ack", { item_key: itemKey }).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["agents", "feed"] }),
  });
}

/** Mark a kicker received: flip the project's brief to submitted (the
 *  same field /intake's Kickstart badge reads), then ack the feed item. */
export function useMarkKickerReceived() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ projectId, itemKey }: { projectId: string; itemKey: string }) => {
      await api.put(`/briefs/${projectId}`, { status: "submitted" });
      await api.post("/agents/items/ack", { item_key: itemKey });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["agents", "feed"] });
      qc.invalidateQueries({ queryKey: ["portfolio"] });
    },
  });
}
